import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React Leaflet
import L from 'leaflet';

// Fix default marker icon issue in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface GPSLocation {
  id: string;
  device_id: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  speed: number | null;
  recorded_at: string;
}

interface GPSDevice {
  id: string;
  device_id: string;
  provider: string;
  device_type: string;
  status: string;
  last_ping: string | null;
  battery_level: number | null;
  signal_strength: number | null;
}

interface GPSDeviceMapProps {
  device: GPSDevice;
  location: GPSLocation | null;
  height?: string;
  showFullAddress?: boolean;
}

// Component to center the map on the device location
const CenterMap: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 16);
    }
  }, [center, map]);

  return null;
};

// Component to handle address geocoding (reverse geocoding)
const AddressDisplay: React.FC<{ location: GPSLocation; showFullAddress?: boolean }> = ({
  location,
  showFullAddress = false,
}) => {
  const [address, setAddress] = React.useState<string>('Loading address...');

  useEffect(() => {
    const geocodeAddress = async () => {
      try {
        // Using OpenStreetMap Nominatim for reverse geocoding (free)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${location.latitude}&lon=${location.longitude}&format=json`
        );
        const data = await response.json();

        if (data.display_name) {
          if (showFullAddress) {
            setAddress(data.display_name);
          } else {
            // Extract just city, state for compact display
            const parts = data.display_name.split(', ');
            const city =
              data.address?.city || data.address?.town || data.address?.village || parts[0];
            const state = data.address?.state || parts[parts.length - 3];
            setAddress(`${city}, ${state}`);
          }
        } else {
          setAddress('Address not available');
        }
      } catch (error) {
        console.error('Error geocoding address:', error);
        setAddress('Address lookup failed');
      }
    };

    if (location.latitude && location.longitude) {
      geocodeAddress();
    }
  }, [location.latitude, location.longitude, showFullAddress]);

  return <span>{address}</span>;
};

const GPSDeviceMap: React.FC<GPSDeviceMapProps> = ({
  device,
  location,
  height = '400px',
  showFullAddress = false,
}) => {
  const mapRef = useRef<any>(null);

  if (!location) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded-lg`} style={{ height }}>
        <div className="text-center">
          <div className="text-gray-500 mb-2">No location data available</div>
          <div className="text-sm text-gray-400">
            Device may be offline or location services disabled
          </div>
        </div>
      </div>
    );
  }

  const center: [number, number] = [location.latitude, location.longitude];

  const getStatusIcon = (status: string) => {
    const iconColor =
      status === 'active' ? '#10b981' : status === 'disabled' ? '#ef4444' : '#6b7280';

    return new L.Icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${iconColor}" width="32" height="32">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      `)}`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });
  };

  return (
    <div className="w-full" style={{ height }}>
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={16}
        style={{ height: '100%', width: '100%' }}
        className="rounded-lg"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <CenterMap center={center} />

        <Marker position={center} icon={getStatusIcon(device.status)}>
          <Popup className="w-80">
            <div className="p-2">
              <h3 className="font-semibold text-lg mb-2">{device.device_id}</h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      device.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : device.status === 'disabled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {device.status}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Provider:</span>
                  <span>{device.provider}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span>{device.device_type.replace('_', ' ')}</span>
                </div>

                {device.battery_level && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Battery:</span>
                    <span>{device.battery_level}%</span>
                  </div>
                )}

                {device.signal_strength && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Signal:</span>
                    <span>{device.signal_strength}%</span>
                  </div>
                )}

                <div className="border-t pt-2 mt-3">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Coordinates:</span>
                    <span className="text-xs font-mono">
                      {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                    </span>
                  </div>

                  {location.speed !== null && (
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600">Speed:</span>
                      <span>{location.speed} mph</span>
                    </div>
                  )}

                  {location.accuracy !== null && (
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600">Accuracy:</span>
                      <span>{location.accuracy}m</span>
                    </div>
                  )}

                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Recorded:</span>
                    <span className="text-xs">
                      {new Date(location.recorded_at).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="text-xs max-w-48 truncate">
                      <AddressDisplay location={location} showFullAddress={showFullAddress} />
                    </span>
                  </div>
                </div>

                <div className="border-t pt-2 mt-3 flex justify-center">
                  <a
                    href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

export default GPSDeviceMap;
